import { useEffect, useMemo, useRef, useCallback } from "react";
import useDataCore from "./hooks/useDataCore";
import InfinityUI from "./components/InfinityUI";
import useQueryParam from "../useQueryParam";
import { NoData } from "components/table/constants/NoData";
import { setParamsKeyName } from "utils";

const useInfiniteScrollCore = ({
  pageName,
  fetchFn,
  fetchByidFn,
  deleteFn,
  restoreFn,
  defaultLimit = 20,
  isTableView = true,
  showLoader = true,
  isCallFetchFn = true,
  defaultParams = null,
}) => {
  const [, setPage] = useQueryParam(setParamsKeyName("page", isTableView), null);

  // ✅ Reset page on mount (replace: no extra Back-button entry)
  useEffect(() => {
    setPage(1, { replace: true });
  }, [setPage]);

  const dataCore = useDataCore({
    pageName,
    fetchFn,
    fetchByidFn,
    deleteFn,
    restoreFn,
    limit: defaultLimit,
    defaultParams,
    showLoader,
    isCallFetchFn,
    isTableView
  });

  const { isLoading, hasMore, hasError, count, loadMore, isFetchApi } =
    dataCore;

  // 🔎 Intersection Observer refs
  const loaderRef = useRef(null);
  const observerRef = useRef(null);
  const debounceRef = useRef(false);
  // latest list state for the observer callback (its closure can be stale)
  const stateRef = useRef({});
  stateRef.current = { hasError, hasMore, isLoading, count, loadMore };

  const setLoaderRef = useCallback(
    (node) => {
      if (observerRef.current) observerRef.current.disconnect();

      if (!node) return;
      loaderRef.current = node;
      const observer = new IntersectionObserver((entries) => {
        const latest = stateRef.current;
        if (latest.hasError || !latest.hasMore || latest.isLoading || !latest.count) return;

        if (entries[0].isIntersecting && !debounceRef.current) {
          debounceRef.current = true;
          latest.loadMore();

          setTimeout(() => {
            debounceRef.current = false;
          }, 1000);
        }
      });

      observer.observe(node);
      observerRef.current = observer;
    },
    // Re-created when the list state changes on purpose: a new observer
    // reports the sentinel at once, so a list that still fits the screen
    // keeps loading pages. The callback itself reads stateRef (never stale).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasMore, isLoading, hasError, loadMore, count],
  );

  // 🎯 Decide content
  const content = useMemo(() => {
    if (!isCallFetchFn || isLoading || !isFetchApi) return null;

    if (count === 0 && !hasMore) {
      return <NoData />;
    }

    if (hasMore) {
      return "Scroll to load more";
    }

    return null;
  }, [isLoading, isFetchApi, count, hasMore, isCallFetchFn]);

  return {
    ...dataCore,
    isShowNoData: !!content,
    paginationUI: (
      <InfinityUI
        content={content}
        isTableView={isTableView}
        setLoaderRef={setLoaderRef}
      />
    ),
  };
};

export default useInfiniteScrollCore;
